import {eventWithTime} from '@rrweb/types';

import {GenerateSignedReplayParamsResponse} from 'app_api/projects/replays/types';

import {HeatMapClickEvent, HeatMapElementPlacement, HeatMapPlayerErrorState} from '../heat-maps/types';

export enum ReplayVersion {
  Unversioned = `undefined`,
  VersionOneBucketMigration = `1`,
}

export type RecordingMetadata = {
  replayId: string;
  time: number;
  distinctId: string;
  name: string;
  avatarUrl: string;
  email: string;
  duration: number;
  replayEnv: string;
  browser: string;
  os: string;
  source: string; // Tells us which SDK the recording came from
  startingURL: string;
  retentionPeriod: number | null;
  ingestedVersions: ReplayVersion[];
  /**
   * When viewing replays from an event, indicates whether the replay is "stitched" or not.
   * A stitched replay means we inferred an event ocurred in this replay via distinct_id and timestamp,
   * but the event itself does not have the $mp_replay_id property.
   */
  isStitchedFromEvent: boolean;
};

export type RecordingEventMetadata = {
  eventName: string;
  eventTime: number;
  quantity: number;
  key: string;

  /**
   * This event has a $mp_replay_id property that matches the replayId of the recording.
   * i.e. this event was guaranteed "observed" in the replay.
   */
  hasMatchingReplayIdProperty: boolean;

  propertyMapping: Map<string, string>;
};

export type RecordingPageViewEvents = {
  replayId: string;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
};

export type RecordingPageViewEventMetadata = {
  currentURL: string;
  eventTime: number;
};

export type RecordingEvents = {
  replayId: string;
  replayEvents: RecordingEventMetadata[];
};

export type RecordingInactivePeriod = {
  startTimeMs: number;
  endTimeMs: number;
};

export enum RecordingPlayerTrackingEvent {
  Close = `[Recording Player] Modal Closed`,
  CopyLink = `[Recording Player] Copy Link`,
  Feedback = `[Recording Player] Feedback Submitted`,
  Finish = `[Recording Player] Replay Finished`,
  ListLoaded = `[Recording Player] Replay List Loaded`,
  ListLoadFailed = `[Recording Player] Replay List Load Failed`,
  Open = `[Recording Player] Modal Opened`,
  ReplayLoaded = `[Recording Player] Replay Data Loaded`,
  Sort = `[Recording Player] Replay List Sorted`,
  ToggleSkipInactivity = `[Recording Player] Toggle Skip Inactivity`,
  ToggleStitching = `[Recording Player] Toggle Stitching`,
  Watch = `[Recording Player] Replay Watched`,
}

export enum RecordingPlayerSource {
  UsersProfile = `User Profile`,
  UsersProfileLink = `User Profile Link`,
  Funnels = `Funnels`,
  Event = `Event`,
  Cohort = `Cohort`,
  InsightsProfile = `InsightsProfile`,
  Home = `Home`,
  HeatMaps = `Heat Maps`,
}

export enum RecordingSpeed {
  Half = 0.5,
  ThreeQuarters = 0.75,
  Normal = 1,
  Double = 2,
  Quadruple = 4,
  Octuple = 8,
  Dectuple = 10,
}

export enum TimeDisplaySetting {
  Hours = `hours`,
  Minutes = `minutes`,
  Seconds = `seconds`,
}

export enum WindowMessageRecipient {
  ParentApp,
  ChildReplay,
}

export type SignedReplayParams = GenerateSignedReplayParamsResponse[`results`];

export type ParentAppMessagePayload = {
  embedReady?: true;
  embedBlocked?: true;
  error?: string;
  loadingRecording?: boolean;
  playerClicked?: true;
  recordingDuration?: number;
  recordingFinished?: boolean;
  recordingInactivePeriods?: RecordingInactivePeriod[];
  recordingProgress?: number;
  recordingSkippingInactive?: boolean;
  recordingSpeed?: RecordingSpeed;
  recordingStarted?: boolean;
  elementPositionsFound?: HeatMapElementPlacement[];
  trackingData?: Obj;
  pageloadEvents?: eventWithTime[];
  trackReplayLoaded?: Obj;
};

export type LoadReplayParams = {
  // authenticated params for the embedded player to be able to pull replay files
  signedReplayParams: SignedReplayParams;
  // target origin that the embedded player should use to send messages to the parent
  targetOrigin: string;
  // retention period of the recording (in days)
  retentionPeriod: number | null;
  heatMapParams?: {
    progress: number;
  };
  ingestedVersions: ReplayVersion[];
};

export enum ReplayErrorStateType {
  NoReplaysAvailable = `no_replay_available`,
}

export enum EmbedErrorStateType {
  NoReplayFound = `no_replay_found`,
  EmbedBlocked = `embed_blocked`,
}

export type HeatMapPlayerErrorStateType = HeatMapPlayerErrorState | EmbedErrorStateType;

export type EmbedReplayPayload = {
  loadReplayParams?: LoadReplayParams;
  recordingPaused?: boolean;
  recordingProgress?: number;
  recordingSpeed?: RecordingSpeed;
  targetOrigin?: string;
  isDarkMode?: boolean;
  heatMapData?: Map<string, HeatMapClickEvent[]>;
  heatMapActive?: boolean;
};

export type ParentAppReplayMessage = {
  recipient: WindowMessageRecipient.ParentApp;
  payload: ParentAppMessagePayload;
};

export type EmbedReplayMessage = {
  recipient: WindowMessageRecipient.ChildReplay;
  payload: EmbedReplayPayload;
};

export type ReplayMessage = EmbedReplayMessage | ParentAppReplayMessage;

export function isReplayWindowMessage(event: MessageEvent): event is MessageEvent<ReplayMessage> {
  return event.data?.recipient !== undefined;
}
